import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import { accountOverviewSchema } from "../../../shared/account-finance/account-overview-contract";
import type { AccountOverview } from "../../../shared/account-finance/account-overview-contract";
import { createFinanceRequestGate } from "./finance-request-gate";
import type { FinanceDirection } from "../../../shared/account-finance/payables-domain";

export function usePayablesWorkspace(date: string, direction: FinanceDirection = "payables") {
  const [data, setData] = useState<AccountOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requests = useRef(createFinanceRequestGate());
  async function load(refresh = false, background = false) {
    const next = requests.current.begin(background ? "poll" : "load");
    if (!next) return;
    if (!background) { setLoading(true); setError(null); }
    try {
      const raw = await apiRequest<unknown>(`/api/account-finance/payables?date=${date}&direction=${direction}&refresh=${refresh ? 1 : 0}`,
        { signal: AbortSignal.any([next.signal, AbortSignal.timeout(100_000)]), cache: "no-store" });
      if (requests.current.owns(next)) { setData(accountOverviewSchema.parse(raw)); setError(null); }
    } catch (failure: unknown) {
      if (requests.current.owns(next)) setError(failure instanceof Error ? failure.message : "Rapor okunamadı.");
    } finally { if (requests.current.owns(next) && !background) setLoading(false); requests.current.finish(next); }
  }
  useEffect(() => { setData(null); void load(); return () => requests.current.cancel(); }, [date, direction]);
  useEffect(() => {
    if (data?.job?.status !== "running" && data?.job?.status !== "pausing") return;
    const timer = setInterval(() => void load(false, true), 8000);
    return () => clearInterval(timer);
  }, [data?.job?.status, date, direction]);
  async function command(action: "start" | "resume" | "pause") {
    const next = requests.current.begin("command");
    if (!next) return;
    setLoading(true); setError(null);
    try {
      const raw = await apiRequest<unknown>("/api/account-finance/transfer", { method: "POST",
        body: JSON.stringify({ date, direction, action }), signal: AbortSignal.any([next.signal, AbortSignal.timeout(15000)]) });
      if (requests.current.owns(next)) setData(accountOverviewSchema.parse(raw));
    } catch (failure) {
      if (requests.current.owns(next)) setError(failure instanceof Error ? failure.message : "Aktarım isteği tamamlanamadı. Durumu yenileyin.");
    } finally { if (requests.current.owns(next)) setLoading(false); requests.current.finish(next); }
  }
  return { data, error, loading, load, command };
}
